import { react } from "@tldraw/state";
import type {
  TLHandle,
  TLImageShape,
  TLShape,
  TLShapeId,
  TLShapePartial,
} from "@tldraw/tlschema";
import { Box, Vec, type VecLike } from "../geometry";
import type { CanvasEditor, SnapLine } from "./editor";
import { Mat } from "./math";
import type { DragShapesInfo, ResizeHandle, ResizeInfo } from "./shape-util";
import {
  StateNode,
  type ClickEventInfo,
  type KeyboardEventInfo,
  type PointerEventInfo,
} from "./state-node";

// Selecting, moving, resizing, rotating and handle dragging. State ids follow the
// select tool Planroom's tools and side effects already refer to ("select.idle",
// "select.brushing", ...), so app subclasses can replace individual states.

const ed = (node: StateNode) => node.editor as unknown as CanvasEditor;
const NUDGE = 1,
  BIG_NUDGE = 10,
  SNAP_DISTANCE = 8; // screen px within which an edge or centre aligns

/** Shapes to move: the selection minus anything inside another selected shape. */
function topLevelSelection(editor: CanvasEditor) {
  const selected = editor.getSelectedShapeIds();
  return editor
    .getSelectedShapes()
    .filter((s) => !selected.some((id) => editor.hasAncestor(s, id)))
    .filter((s) => !editor.isShapeOrAncestorLocked(s));
}

export class Idle extends StateNode {
  static override id = "idle";
  override onEnter() {
    ed(this).setCursor({ type: "default", rotation: 0 });
  }
  override onPointerMove(info: PointerEventInfo) {
    const editor = ed(this);
    editor.setHoveredShape(
      info.target === "shape"
        ? editor.getOutermostSelectableShape(info.shape).id
        : null,
    );
    editor.setCursor({ type: cursorFor(info), rotation: 0 });
  }
  override onPointerDown(info: PointerEventInfo) {
    const editor = ed(this);
    if (info.target === "selection" && info.handle === "rotate") {
      if (!editor.getIsReadonly())
        this.parent.transition("pointing_rotate_handle", info);
      return;
    }
    if (info.target === "selection" && info.handle) {
      if (!editor.getIsReadonly())
        this.parent.transition("pointing_resize_handle", info);
      return;
    }
    if (info.target === "handle") {
      if (!editor.getIsReadonly())
        this.parent.transition("pointing_handle", info);
      return;
    }
    if (info.target === "shape") {
      this.parent.transition("pointing_shape", info);
      return;
    }
    if (info.target === "selection") {
      this.parent.transition("pointing_selection", info);
      return;
    }
    this.parent.transition("pointing_canvas", info);
  }
  override onDoubleClick(info: ClickEventInfo) {
    const editor = ed(this);
    if (info.phase !== "down" || editor.getIsReadonly()) return;
    const shape =
      info.target === "shape"
        ? info.shape
        : info.target === "selection"
          ? editor.getOnlySelectedShape()
          : undefined;
    if (!shape || editor.isShapeOrAncestorLocked(shape)) return;
    const util = editor.getUtil(shape);
    const change = util.onDoubleClick?.(shape) as TLShapePartial | void;
    if (change) {
      editor.markHistoryStoppingPoint("double click");
      editor.updateShape({
        ...change,
        id: shape.id,
        type: shape.type,
      } as never);
      return;
    }
    if (util.canEdit?.(shape)) startEditing(this, shape);
  }
  override onKeyDown(info: KeyboardEventInfo) {
    const editor = ed(this);
    const mod = info.metaKey || info.ctrlKey;
    if ((info.key === "Delete" || info.key === "Backspace") && !mod) {
      if (editor.getIsReadonly() || !editor.getSelectedShapeIds().length)
        return;
      editor.markHistoryStoppingPoint("delete");
      editor.deleteShapes(editor.getSelectedShapeIds());
    } else if (info.key === "Escape") editor.selectNone();
    else if (info.key === "Enter") {
      const shape = editor.getOnlySelectedShape();
      if (
        shape &&
        editor.getUtil(shape).canEdit?.(shape) &&
        !editor.getIsReadonly()
      )
        startEditing(this, shape);
    } else if (info.key.startsWith("Arrow") && !editor.getIsReadonly()) {
      const step = (info.shiftKey ? BIG_NUDGE : NUDGE) / editor.getZoomLevel();
      const delta = {
        ArrowLeft: new Vec(-step, 0),
        ArrowRight: new Vec(step, 0),
        ArrowUp: new Vec(0, -step),
        ArrowDown: new Vec(0, step),
      }[info.key];
      const shapes = topLevelSelection(editor);
      if (delta && shapes.length) {
        if (!this.nudging) {
          editor.markHistoryStoppingPoint("Move with arrow keys", { hold: true });
          this.nudging = true;
        }
        editor.nudgeShapes(shapes, delta);
      }
    }
  }
  override onKeyRepeat(info: KeyboardEventInfo) {
    if (info.key.startsWith("Arrow")) this.onKeyDown(info);
  }
  private nudging = false;
  override onKeyUp(info: KeyboardEventInfo) {
    if (info.key.startsWith("Arrow")) {
      this.nudging = false;
      ed(this).commitHistoryStoppingPoint();
    }
  }
  override onExit() { this.nudging = false; }
}

function cursorFor(info: PointerEventInfo) {
  if (info.target !== "selection" || !info.handle) return "default";
  if (info.handle === "rotate") return "grab";
  if (info.handle === "left" || info.handle === "right") return "ew-resize";
  if (info.handle === "top" || info.handle === "bottom") return "ns-resize";
  return info.handle === "top_left" || info.handle === "bottom_right"
    ? "nwse-resize"
    : "nesw-resize";
}

function startEditing(node: StateNode, shape: TLShape) {
  const editor = ed(node);
  editor.select(shape.id);
  editor.setEditingShape(shape.id);
  node.parent.transition("editing_shape", { shape });
}

export class PointingCanvas extends StateNode {
  static override id = "pointing_canvas";
  override onEnter(info: PointerEventInfo) {
    if (!info.shiftKey) ed(this).selectNone();
  }
  override onPointerMove(info: PointerEventInfo) {
    if (ed(this).inputs.getIsDragging())
      this.parent.transition("brushing", info);
  }
  override onPointerUp() {
    this.parent.transition("idle");
  }
  override onCancel() {
    this.parent.transition("idle");
  }
}

/** A double-click arriving mid-press is replayed once the press ends in idle. */
function replayDoubleClick(node: StateNode, click: ClickEventInfo | undefined) {
  if (click && node.parent.getCurrent()?.id === "idle")
    node.parent.getCurrent()!.handleEvent(click);
}

export class PointingShape extends StateNode {
  static override id = "pointing_shape";
  protected shape?: TLShape;
  protected wasSelected = false;
  private doubleClick?: ClickEventInfo;
  override onDoubleClick(info: ClickEventInfo) {
    if (info.phase === "down") this.doubleClick = info;
  }
  override onEnter(info: PointerEventInfo & { target: "shape" }) {
    this.doubleClick = undefined;
    const editor = ed(this);
    const shape = editor.getOutermostSelectableShape(info.shape);
    this.shape = shape;
    const selected = editor.getSelectedShapeIds();
    this.wasSelected =
      selected.includes(shape.id) ||
      !!editor.findShapeAncestor(shape, (parent) =>
        selected.includes(parent.id),
      );
    if (!this.wasSelected)
      editor.setSelectedShapes(
        info.shiftKey ? [...selected, shape.id] : [shape.id],
      );
  }
  override onPointerMove(info: PointerEventInfo) {
    const editor = ed(this);
    if (!editor.inputs.getIsDragging() || editor.getIsReadonly()) return;
    if (!topLevelSelection(editor).length) return;
    this.parent.transition("translating", info);
  }
  override onPointerUp(info: PointerEventInfo) {
    const editor = ed(this);
    const shape = this.shape;
    if (shape && this.wasSelected) {
      const selected = editor.getSelectedShapeIds();
      if (info.shiftKey)
        editor.setSelectedShapes(selected.filter((id) => id !== shape.id));
      else if (selected.length > 1) editor.select(shape.id);
    }
    this.parent.transition("idle");
    replayDoubleClick(this, this.doubleClick);
  }
  override onCancel() {
    this.parent.transition("idle");
  }
  override onInterrupt() {
    this.parent.transition("idle");
  }
}

export class PointingSelection extends StateNode {
  static override id = "pointing_selection";
  private doubleClick?: ClickEventInfo;
  override onEnter() {
    this.doubleClick = undefined;
  }
  override onPointerMove(info: PointerEventInfo) {
    const editor = ed(this);
    if (editor.inputs.getIsDragging() && !editor.getIsReadonly())
      this.parent.transition("translating", info);
  }
  override onPointerUp(info: PointerEventInfo) {
    const editor = ed(this);
    if (!info.shiftKey) {
      // A press inside the selection box but on another shape selects that shape.
      const hit = editor.getShapeAtPoint(info.pagePoint, {
        hitInside: true,
        hitLabels: true,
      });
      if (hit) editor.select(editor.getOutermostSelectableShape(hit).id);
      else if (!editor.getSelectedShapes().length) editor.selectNone();
    }
    this.parent.transition("idle");
    replayDoubleClick(this, this.doubleClick);
  }
  override onDoubleClick(info: ClickEventInfo) {
    if (info.phase === "down") this.doubleClick = info;
  }
  override onCancel() {
    this.parent.transition("idle");
  }
}

export class Brushing extends StateNode {
  static override id = "brushing";
  private initial: TLShapeId[] = [];
  override onEnter(info: PointerEventInfo) {
    const editor = ed(this);
    this.initial = info.shiftKey ? editor.getSelectedShapeIds() : [];
    this.update();
  }
  override onExit() {
    ed(this).updateInstanceState({ brush: null });
  }
  override onPointerMove() {
    this.update();
  }
  private update() {
    const editor = ed(this);
    const brush = Box.FromPoints([
      editor.inputs.getOriginPagePoint(),
      editor.inputs.getCurrentPagePoint(),
    ]);
    editor.updateInstanceState({ brush: brush.toJson() });
    const polygon = brush.corners;
    const hits = new Set<TLShapeId>(this.initial);
    for (const shape of editor.getCurrentPageShapesSorted()) {
      if (editor.isShapeHidden(shape) || editor.isShapeOrAncestorLocked(shape))
        continue;
      const outer = editor.getOutermostSelectableShape(shape);
      if (outer.id !== shape.id || hits.has(shape.id)) continue;
      if ([...hits].some((id) => editor.hasAncestor(shape, id))) continue;
      const local = editor
        .getShapePageTransform(shape)
        .invert()
        .applyToPoints(polygon);
      const geometry = editor.getShapeGeometry(shape) as unknown as {
        overlapsPolygon?(p: Vec[]): boolean;
        bounds: Box;
      };
      const touches = geometry.overlapsPolygon
        ? geometry.overlapsPolygon(local)
        : Box.Collides(geometry.bounds, Box.FromPoints(local));
      // Frames are selected only when the brush wraps them completely.
      if (editor.isShapeFrameLike(shape)) {
        const bounds = editor.getShapePageBounds(shape);
        if (bounds && Box.Contains(brush, bounds)) hits.add(shape.id);
        continue;
      }
      if (touches) hits.add(shape.id);
    }
    editor.setSelectedShapes([...hits]);
  }
  override onPointerUp() {
    this.parent.transition("idle");
  }
  override onCancel() {
    ed(this).setSelectedShapes(this.initial);
    this.parent.transition("idle");
  }
}

type Snapshot = { shape: TLShape; page: Mat };

export class Translating extends StateNode {
  static override id = "translating";
  private mark = "";
  private moving: Snapshot[] = [];
  private dragInfo!: DragShapesInfo;
  /** Page bounds of the moving shapes at the start, and of what they can align with. */
  private startBounds?: Box;
  private snapTargets: Box[] = [];
  override onEnter() {
    const editor = ed(this);
    this.mark = editor.markHistoryStoppingPoint("translating", { hold: true });
    for (const shape of topLevelSelection(editor)) {
      const partial = editor.getUtil(shape).onTranslateStart?.(shape);
      if (partial) editor.updateShapes([partial]);
    }
    this.moving = topLevelSelection(editor).map((shape) => ({
      shape,
      page: editor.getShapePageTransform(shape),
    }));
    const firstParent =
      this.moving[0] && editor.getShape(this.moving[0].shape.parentId);
    this.dragInfo = {
      initialParentIds: new Map(
        this.moving.map(({ shape }) => [shape.id, shape.parentId]),
      ),
      initialIndices: new Map(
        this.moving.map(({ shape }) => [shape.id, shape.index]),
      ),
      initialDraggingOverShapeId: firstParent?.id ?? null,
      prevDraggingOverShapeId: firstParent?.id ?? null,
      nextDraggingOverShapeId: null,
    };
    const boxes = this.moving
      .map(({ shape }) => editor.getShapePageBounds(shape))
      .filter((b): b is Box => !!b);
    this.startBounds = boxes.length ? Box.Common(boxes) : undefined;
    this.snapTargets = snapTargets(
      editor,
      this.moving.map(({ shape }) => shape),
    );
    editor.setCursor({ type: "move", rotation: 0 });
    editor.setHoveredShape(null);
    this.update();
  }
  override onExit() {
    ed(this).setSnapLines([]);
  }
  override onPointerMove() {
    this.update();
  }
  private update() {
    const editor = ed(this);
    let delta = Vec.Sub(
      editor.inputs.getCurrentPagePoint(),
      editor.inputs.getOriginPagePoint(),
    );
    // Shift keeps the move on one axis; snapping then only acts along it.
    let lock: "x" | "y" | null = null;
    if (editor.inputs.shiftKey) {
      lock = Math.abs(delta.x) > Math.abs(delta.y) ? "x" : "y";
      delta = lock === "x" ? new Vec(delta.x, 0) : new Vec(0, delta.y);
    }
    // Edges and centres align with other shapes; Ctrl inverts the setting.
    const snapping = editor.user.getIsSnapMode() !== editor.inputs.ctrlKey;
    if (snapping && this.startBounds) {
      const snap = snapTranslation(
        this.startBounds,
        delta,
        this.snapTargets,
        SNAP_DISTANCE / editor.getZoomLevel(),
        lock,
      );
      delta = snap.delta;
      editor.setSnapLines(snap.lines);
    } else editor.setSnapLines([]);
    editor.updateShapes(
      this.moving.map(({ shape, page }) => {
        const target = page.point().add(delta);
        const local = editor.getPointInParentSpace(shape.id, target);
        return { id: shape.id, type: shape.type, x: local.x, y: local.y };
      }),
    );
  }
  override onPointerUp() {
    this.drop();
    this.parent.transition("idle");
  }
  /** Moving into or out of a sheet hands the shapes to that sheet's rules. */
  private drop() {
    const editor = ed(this);
    const moving = new Set(this.moving.map((m) => m.shape.id));
    const byTarget = new Map<string, TLShape[]>();
    for (const { shape } of this.moving) {
      const current = editor.getShape(shape.id);
      if (!current) continue;
      const center = editor.getShapePageBounds(current)!.center;
      const frame = editor
        .getCurrentPageShapesSorted()
        .slice()
        .reverse()
        .find(
          (s) =>
            s.id !== current.id &&
            !moving.has(s.id) &&
            ![...moving].some(
              (id) => s.id === id || editor.hasAncestor(s, id),
            ) &&
            editor.isShapeFrameLike(s) &&
            !editor.isShapeHidden(s) &&
            editor.getUtil(s).canReceiveNewChildrenOfType?.(s, current.type) &&
            editor
              .getShapeGeometry(s)
              .bounds.containsPoint(editor.getPointInShapeSpace(s, center)),
        );
      const key = frame?.id ?? "";
      if (
        (frame?.id ?? null) ===
        (editor.getShape(current.parentId) ? current.parentId : null)
      )
        continue;
      (byTarget.get(key) ?? byTarget.set(key, []).get(key)!).push(current);
    }
    for (const [target, shapes] of byTarget) {
      // Leaving the current parent first: its rules may keep the shapes.
      const leaving = new Map<string, TLShape[]>();
      for (const shape of shapes) {
        const parent = editor.getShape(shape.parentId);
        if (parent)
          (
            leaving.get(parent.id) ?? leaving.set(parent.id, []).get(parent.id)!
          ).push(shape);
      }
      for (const [parentId, list] of leaving) {
        const parent = editor.getShape(parentId as TLShapeId)!;
        const util = editor.getUtil(parent);
        const allowed = list.filter(
          (s) => util.canRemoveChildrenOfType?.(parent, s.type) !== false,
        );
        if (!allowed.length) continue;
        if (util.onDragShapesOut)
          util.onDragShapesOut(parent, allowed, {
            ...this.dragInfo,
            nextDraggingOverShapeId: (target || null) as TLShapeId | null,
          });
        else editor.reparentShapes(allowed, editor.getCurrentPageId());
      }
      const stillFree = shapes
        .map((s) => editor.getShape(s.id)!)
        .filter((s) => s && !editor.getShape(s.parentId));
      if (!target || !stillFree.length) continue;
      const frame = editor.getShape(target as TLShapeId)!;
      const util = editor.getUtil(frame);
      if (util.onDragShapesIn)
        util.onDragShapesIn(frame, stillFree, this.dragInfo);
      else editor.reparentShapes(stillFree, frame.id);
    }
  }
  override onCancel() {
    ed(this).bailToMark(this.mark);
    this.parent.transition("idle");
  }
  override onKeyDown(info: KeyboardEventInfo) {
    if (info.key === "Shift" || info.key === "Control") this.update();
    if (info.key === "Escape") this.onCancel();
  }
  override onKeyUp(info: KeyboardEventInfo) {
    if (info.key === "Shift" || info.key === "Control") this.update();
  }
}

/** Page bounds of the visible shapes a moving selection can align with. */
function snapTargets(editor: CanvasEditor, moving: TLShape[]): Box[] {
  const ids = new Set(moving.map((s) => s.id));
  const viewport = editor.getViewportPageBounds();
  const out: Box[] = [];
  for (const shape of editor.getCurrentPageShapesSorted()) {
    if (ids.has(shape.id) || editor.isShapeHidden(shape)) continue;
    if ([...ids].some((id) => editor.hasAncestor(shape, id))) continue;
    const bounds = editor.getShapePageBounds(shape);
    if (bounds && Box.Collides(viewport, bounds)) out.push(bounds);
  }
  return out;
}

/**
 * Adjust a translation so the nearest edge or centre of the moved bounds lands
 * on a target's edge or centre within `distance`, per axis, and describe the
 * guides through the aligned points.
 */
export function snapTranslation(
  start: Box,
  delta: Vec,
  targets: Box[],
  distance: number,
  lock: "x" | "y" | null = null,
): { delta: Vec; lines: SnapLine[] } {
  const moved = new Box(start.x + delta.x, start.y + delta.y, start.w, start.h);
  const nearest = (own: number[], others: number[]) => {
    let best: number | null = null;
    for (const a of own)
      for (const b of others) {
        const offset = b - a;
        if (
          Math.abs(offset) <= distance &&
          (best === null || Math.abs(offset) < Math.abs(best))
        )
          best = offset;
      }
    return best;
  };
  const xs = (b: Box) => [b.minX, b.midX, b.maxX];
  const ys = (b: Box) => [b.minY, b.midY, b.maxY];
  const dx = lock === "y" ? null : nearest(xs(moved), targets.flatMap(xs));
  const dy = lock === "x" ? null : nearest(ys(moved), targets.flatMap(ys));
  const snapped = new Vec(delta.x + (dx ?? 0), delta.y + (dy ?? 0));
  const result = new Box(
    start.x + snapped.x,
    start.y + snapped.y,
    start.w,
    start.h,
  );
  const lines: SnapLine[] = [];
  const near = (a: number, b: number) => Math.abs(a - b) < 1e-6;
  const boxes = [result, ...targets];
  if (dx !== null)
    for (const x of xs(result)) {
      if (!targets.some((t) => xs(t).some((v) => near(v, x)))) continue;
      const points: VecLike[] = [];
      for (const b of boxes)
        if (xs(b).some((v) => near(v, x)))
          for (const y of ys(b))
            if (!points.some((p) => near(p.y, y))) points.push({ x, y });
      lines.push({ id: "x:" + x, points });
    }
  if (dy !== null)
    for (const y of ys(result)) {
      if (!targets.some((t) => ys(t).some((v) => near(v, y)))) continue;
      const points: VecLike[] = [];
      for (const b of boxes)
        if (ys(b).some((v) => near(v, y)))
          for (const x of xs(b))
            if (!points.some((p) => near(p.x, x))) points.push({ x, y });
      lines.push({ id: "y:" + y, points });
    }
  return { delta: snapped, lines };
}

export class PointingResizeHandle extends StateNode {
  static override id = "pointing_resize_handle";
  private start?: PointerEventInfo;
  override onEnter(info: PointerEventInfo) {
    this.start = info;
  }
  override onPointerMove() {
    if (ed(this).inputs.getIsDragging())
      this.parent.transition("resizing", this.start);
  }
  override onPointerUp() {
    this.parent.transition("idle");
  }
  override onCancel() {
    this.parent.transition("idle");
  }
}

type ResizeStart = {
  shape: TLShape;
  page: Mat;
  bounds: Box;
};

export class Resizing extends StateNode {
  static override id = "resizing";
  private mark = "";
  private handle: ResizeHandle = "bottom_right";
  private shapes: ResizeStart[] = [];
  /** Selection frame: its transform from frame space to page, and its size. */
  private frame = { toPage: Mat.Identity(), box: new Box() };
  override onEnter(info: PointerEventInfo & { target: "selection" }) {
    const editor = ed(this);
    this.mark = editor.markHistoryStoppingPoint("resizing", { hold: true });
    this.handle = info.handle as ResizeHandle;
    this.shapes = topLevelSelection(editor).map((shape) => ({
      shape,
      page: editor.getShapePageTransform(shape),
      bounds: Box.From(editor.getShapeGeometry(shape).bounds),
    }));
    if (this.shapes.length === 1) {
      const { page, bounds } = this.shapes[0];
      this.frame = { toPage: page, box: bounds };
    } else {
      const box = Box.From(editor.getSelectionPageBounds()!);
      this.frame = { toPage: Mat.Identity(), box };
    }
    editor.setHoveredShape(null);
  }
  override onPointerMove() {
    this.update();
  }
  private update() {
    const editor = ed(this);
    const { toPage, box } = this.frame;
    const fromPage = toPage.invert();
    const point = fromPage.applyToPoint(editor.inputs.getCurrentPagePoint());
    const h = this.handle;
    let { minX, minY, maxX, maxY } = box;
    if (h.includes("left")) minX = point.x;
    if (h.includes("right")) maxX = point.x;
    if (h.includes("top")) minY = point.y;
    if (h.includes("bottom")) maxY = point.y;
    if (editor.inputs.altKey) {
      // Resize from the centre.
      if (h.includes("left")) maxX = box.maxX + (box.minX - minX);
      if (h.includes("right")) minX = box.minX - (maxX - box.maxX);
      if (h.includes("top")) maxY = box.maxY + (box.minY - minY);
      if (h.includes("bottom")) minY = box.minY - (maxY - box.maxY);
    }
    let sx = (maxX - minX) / (box.w || 1),
      sy = (maxY - minY) / (box.h || 1);
    const locked =
      editor.inputs.shiftKey ||
      this.shapes.some(({ shape }) =>
        editor.getUtil(shape).isAspectRatioLocked?.(shape),
      );
    if (locked) {
      const edgeOnly =
        h === "left" || h === "right"
          ? "x"
          : h === "top" || h === "bottom"
            ? "y"
            : null;
      const s =
        edgeOnly === "x"
          ? sx
          : edgeOnly === "y"
            ? sy
            : Math.abs(sx) > Math.abs(sy)
              ? sx
              : sy;
      sx = Math.sign(sx || 1) * Math.abs(s);
      sy = Math.sign(sy || 1) * Math.abs(s);
      if (!h.includes("left") && !h.includes("right"))
        minX = box.midX - (box.w * sx) / 2;
      else if (h.includes("left")) minX = box.maxX - box.w * sx;
      if (!h.includes("top") && !h.includes("bottom"))
        minY = box.midY - (box.h * sy) / 2;
      else if (h.includes("top")) minY = box.maxY - box.h * sy;
      maxX = minX + box.w * sx;
      maxY = minY + box.h * sy;
    }
    // Scale about the anchor corner in frame space.
    const anchor = new Vec(
      sx >= 0 ? Math.min(minX, maxX) : Math.max(minX, maxX),
      sy >= 0 ? Math.min(minY, maxY) : Math.max(minY, maxY),
    );
    const scale = (p: Vec) =>
      new Vec(
        anchor.x + (p.x - box.minX) * sx,
        anchor.y + (p.y - box.minY) * sy,
      );
    const updates: TLShapePartial[] = [];
    for (const { shape, page, bounds } of this.shapes) {
      const util = editor.getUtil(shape);
      if (util.canResize?.(shape) === false) continue;
      // The shape's new top-left corner (of its bounds) in page space.
      const shapeToFrame = fromPage.multiply(page);
      const corner = shapeToFrame.applyToPoint({
        x: bounds.minX,
        y: bounds.minY,
      });
      const newCornerPage = toPage.applyToPoint(scale(corner));
      // Convert from bounds corner to shape origin, then to parent space.
      const originOffset = new Mat(
        page.a,
        page.b,
        page.c,
        page.d,
        0,
        0,
      ).applyToPoint({
        x: -bounds.minX * sx,
        y: -bounds.minY * sy,
      });
      const originPage = newCornerPage.add(originOffset);
      const newPoint = editor.getPointInParentSpace(shape.id, originPage);
      const info: ResizeInfo = {
        newPoint,
        handle: this.handle,
        mode: this.shapes.length > 1 ? "scale_shape" : "resize_bounds",
        scaleX: this.shapes.length > 1 ? Math.abs(sx) : sx,
        scaleY: this.shapes.length > 1 ? Math.abs(sy) : sy,
        initialBounds: bounds,
        initialShape: shape,
      };
      const partial =
        (util.onResize?.(shape, info) as TLShapePartial | undefined) ??
        ({
          id: shape.id,
          type: shape.type,
          x: newPoint.x,
          y: newPoint.y,
        } as TLShapePartial);
      updates.push({
        ...partial,
        id: shape.id,
        type: shape.type,
      } as TLShapePartial);
      // Containers that do not scale their contents keep them in place on the page.
      if (util.canResizeChildren?.(shape) === false) {
        const next = { ...shape, ...partial } as TLShape;
        const moved = Mat.Compose(next.x, next.y, next.rotation)
          .invert()
          .multiply(Mat.Compose(shape.x, shape.y, shape.rotation));
        for (const childId of editor.getSortedChildIdsForParent(shape.id)) {
          const child = editor.getShape(childId)!;
          const p = moved.applyToPoint(this.initialChild(child));
          updates.push({ id: child.id, type: child.type, x: p.x, y: p.y });
        }
      }
    }
    editor.updateShapes(updates);
  }
  private childStart = new Map<TLShapeId, Vec>();
  private initialChild(child: TLShape) {
    if (!this.childStart.has(child.id))
      this.childStart.set(child.id, new Vec(child.x, child.y));
    return this.childStart.get(child.id)!;
  }
  override onExit() {
    this.childStart.clear();
  }
  override onPointerUp() {
    this.parent.transition("idle");
  }
  override onCancel() {
    ed(this).bailToMark(this.mark);
    this.parent.transition("idle");
  }
  override onKeyDown(info: KeyboardEventInfo) {
    if (info.key === "Shift" || info.key === "Alt") this.update();
    if (info.key === "Escape") this.onCancel();
  }
  override onKeyUp(info: KeyboardEventInfo) {
    if (info.key === "Shift" || info.key === "Alt") this.update();
  }
}

export class PointingRotateHandle extends StateNode {
  static override id = "pointing_rotate_handle";
  private start?: PointerEventInfo;
  override onEnter(info: PointerEventInfo) {
    this.start = info;
  }
  override onPointerMove() {
    if (ed(this).inputs.getIsDragging())
      this.parent.transition("rotating", this.start);
  }
  override onPointerUp() {
    this.parent.transition("idle");
  }
}

export class Rotating extends StateNode {
  static override id = "rotating";
  private mark = "";
  private center = new Vec();
  private start = 0;
  private shapes: Array<{ shape: TLShape; page: Mat }> = [];
  override onEnter() {
    const editor = ed(this);
    this.mark = editor.markHistoryStoppingPoint("rotating", { hold: true });
    this.shapes = topLevelSelection(editor).map((shape) => ({
      shape,
      page: editor.getShapePageTransform(shape),
    }));
    this.center = Vec.From(editor.getSelectionPageBounds()!.center);
    this.start = angle(this.center, editor.inputs.getOriginPagePoint());
    editor.setCursor({ type: "grabbing", rotation: 0 });
  }
  override onPointerMove() {
    const editor = ed(this);
    let delta =
      angle(this.center, editor.inputs.getCurrentPagePoint()) - this.start;
    if (editor.inputs.shiftKey)
      delta = Math.round(delta / (Math.PI / 12)) * (Math.PI / 12);
    const turn = Mat.Compose(this.center.x, this.center.y, delta).multiply(
      Mat.Compose(-this.center.x, -this.center.y),
    );
    editor.updateShapes(
      this.shapes.map(({ shape, page }) => {
        const next = turn.multiply(page);
        const parent = editor.getShape(shape.parentId);
        const parentPage = parent
          ? editor.getShapePageTransform(parent)
          : Mat.Identity();
        const local = parentPage.invert().multiply(next);
        return {
          id: shape.id,
          type: shape.type,
          x: local.e,
          y: local.f,
          rotation: local.rotation(),
        };
      }) as never,
    );
  }
  override onPointerUp() {
    this.parent.transition("idle");
  }
  override onCancel() {
    ed(this).bailToMark(this.mark);
    this.parent.transition("idle");
  }
}
const angle = (center: VecLike, p: VecLike) =>
  Math.atan2(p.y - center.y, p.x - center.x);

export class PointingHandle extends StateNode {
  static override id = "pointing_handle";
  private start?: PointerEventInfo;
  private doubleClick?: ClickEventInfo;
  override onEnter(info: PointerEventInfo) {
    this.start = info;
    this.doubleClick = undefined;
  }
  override onPointerMove() {
    if (ed(this).inputs.getIsDragging())
      this.parent.transition("dragging_handle", this.start);
  }
  // Double-clicking a handle (an arrow's midpoint) acts on its shape.
  override onDoubleClick(info: ClickEventInfo) {
    const shape = (this.start as { shape?: TLShape } | undefined)?.shape;
    if (info.phase === "down" && shape)
      this.doubleClick = { ...info, target: "shape", shape } as ClickEventInfo;
  }
  override onPointerUp() {
    this.parent.transition("idle");
    replayDoubleClick(this, this.doubleClick);
  }
}

export class DraggingHandle extends StateNode {
  static override id = "dragging_handle";
  private mark = "";
  private shape?: TLShape;
  private handle?: TLHandle;
  override onEnter(info: PointerEventInfo & { target: "handle" }) {
    const editor = ed(this);
    this.mark = editor.markHistoryStoppingPoint("dragging handle", { hold: true });
    this.shape = info.shape;
    this.handle = info.handle;
    const util = editor.getUtil(info.shape);
    const start = util.onHandleDragStart?.(info.shape, {
      handle: info.handle,
      isPrecise: false,
    });
    if (start)
      editor.updateShape({
        ...start,
        id: info.shape.id,
        type: info.shape.type,
      });
    this.update();
  }
  override onPointerMove() {
    this.update();
  }
  private update() {
    const editor = ed(this);
    const shape = this.shape && editor.getShape(this.shape.id);
    if (!shape || !this.handle) return;
    const local = editor.getPointInShapeSpace(
      shape,
      editor.inputs.getCurrentPagePoint(),
    );
    const handle = { ...this.handle, x: local.x, y: local.y };
    const partial = editor.getUtil(shape).onHandleDrag?.(shape, {
      handle,
      isPrecise: editor.inputs.altKey,
      initial: this.shape,
    });
    if (partial)
      editor.updateShape({ ...partial, id: shape.id, type: shape.type });
  }
  override onPointerUp() {
    const editor = ed(this);
    const shape = this.shape && editor.getShape(this.shape.id);
    if (shape && this.handle) {
      const end = editor
        .getUtil(shape)
        .onHandleDragEnd?.(shape, {
          handle: this.handle,
          isPrecise: false,
          initial: this.shape,
        });
      if (end) editor.updateShape({ ...end, id: shape.id, type: shape.type });
    }
    this.parent.transition("idle");
  }
  override onCancel() {
    const editor = ed(this);
    const shape = this.shape && editor.getShape(this.shape.id);
    if (shape && this.handle)
      editor
        .getUtil(shape)
        .onHandleDragCancel?.(shape, {
          handle: this.handle,
          isPrecise: false,
          initial: this.shape,
        });
    editor.bailToMark(this.mark);
    this.parent.transition("idle");
  }
}

export class EditingShape extends StateNode {
  static override id = "editing_shape";
  override onPointerDown(info: PointerEventInfo) {
    const editor = ed(this);
    const editing = editor.getEditingShapeId();
    if (info.target === "shape" && info.shape.id === editing) return;
    // A press elsewhere ends editing and is handled as a normal press.
    editor.setEditingShape(null);
    this.parent.transition("idle");
    this.parent.getCurrent()?.handleEvent(info);
  }
  override onKeyDown(info: KeyboardEventInfo) {
    if (info.key === "Escape") this.stop();
  }
  override onCancel() {
    this.stop();
  }
  override onComplete() {
    this.stop();
  }
  private stop() {
    const editor = ed(this);
    const id = editor.getEditingShapeId();
    this.parent.transition("idle");
    if (id && editor.getShape(id)) editor.select(id);
  }
  override onExit() {
    const editor = ed(this);
    const id = editor.getEditingShapeId();
    editor.setEditingShape(null);
    // Text left empty when editing ends is removed.
    const shape = id && editor.getShape(id);
    if (
      shape &&
      shape.type === "text" &&
      !editor.getUtil(shape).getText?.(shape)
    )
      editor.deleteShapes([shape.id]);
  }
}

// ----- crop mode -----
// Entered as "select.crop.idle" with the image set as the cropping shape. Edges
// and corners crop; dragging inside slides the image under the crop.

const MIN_CROP = 8; // smallest cropped size, in shape units

type Crop = NonNullable<TLImageShape["props"]["crop"]>;
const FULL: Crop = { topLeft: { x: 0, y: 0 }, bottomRight: { x: 1, y: 1 } };

/** The whole image's box in the shape's own space, given its current crop. */
function fullImageBox(shape: TLImageShape) {
  const c = shape.props.crop ?? FULL;
  const fullW = shape.props.w / Math.max(0.0001, c.bottomRight.x - c.topLeft.x);
  const fullH = shape.props.h / Math.max(0.0001, c.bottomRight.y - c.topLeft.y);
  // Flipped images show the crop window mirrored within the shape.
  const left = shape.props.flipX ? 1 - c.bottomRight.x : c.topLeft.x;
  const top = shape.props.flipY ? 1 - c.bottomRight.y : c.topLeft.y;
  return new Box(-left * fullW, -top * fullH, fullW, fullH);
}
/** The crop for a visible box (shape space) over the whole image's box. */
function cropFor(shape: TLImageShape, visible: Box, full: Box): Crop {
  let x0 = (visible.minX - full.minX) / full.w,
    x1 = (visible.maxX - full.minX) / full.w,
    y0 = (visible.minY - full.minY) / full.h,
    y1 = (visible.maxY - full.minY) / full.h;
  if (shape.props.flipX) [x0, x1] = [1 - x1, 1 - x0];
  if (shape.props.flipY) [y0, y1] = [1 - y1, 1 - y0];
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  return {
    topLeft: { x: clamp(x0), y: clamp(y0) },
    bottomRight: { x: clamp(x1), y: clamp(y1) },
  };
}
const croppingShape = (editor: CanvasEditor) => {
  const id = editor.getCroppingShapeId();
  const shape = id && editor.getShape(id);
  return shape && shape.type === "image" ? (shape as TLImageShape) : undefined;
};

class CropIdle extends StateNode {
  static override id = "idle";
  private stop?: () => void;
  override onEnter() {
    const editor = ed(this);
    editor.setCursor({ type: "default", rotation: 0 });
    // Ending crop from outside (the app's Done and Cancel) returns to selecting.
    this.stop = react("crop ended", () => {
      if (!editor.getCroppingShapeId()) queueMicrotask(() => this.leave());
    });
  }
  override onExit() {
    this.stop?.();
    this.stop = undefined;
  }
  private leave(info?: PointerEventInfo) {
    const select = this.parent.parent;
    if (!this.getIsActive() || !select) return;
    ed(this).setCroppingShape(null);
    select.transition("idle");
    if (info) select.getCurrent()?.handleEvent(info);
  }
  override onPointerMove(info: PointerEventInfo) {
    ed(this).setCursor({ type: cursorFor(info), rotation: 0 });
  }
  override onPointerDown(info: PointerEventInfo) {
    const editor = ed(this);
    const shape = croppingShape(editor);
    if (!shape || editor.getIsReadonly()) return this.leave(info);
    if (info.target === "selection" && info.handle && info.handle !== "rotate") {
      this.parent.transition("cropping", info);
      return;
    }
    const inside =
      (info.target === "shape" && info.shape.id === shape.id) ||
      (info.target === "selection" && !info.handle);
    if (inside) this.parent.transition("translating_crop", info);
    else this.leave(info);
  }
  override onKeyDown(info: KeyboardEventInfo) {
    if (info.key === "Escape" || info.key === "Enter") this.leave();
  }
  override onCancel() {
    this.leave();
  }
}

class TranslatingCrop extends StateNode {
  static override id = "translating_crop";
  private mark = "";
  private start?: { shape: TLImageShape; full: Box; point: Vec };
  override onEnter() {
    const editor = ed(this);
    const shape = croppingShape(editor);
    if (!shape) return this.parent.transition("idle");
    this.mark = editor.markHistoryStoppingPoint("move image in crop", { hold: true });
    this.start = {
      shape,
      full: fullImageBox(shape),
      point: Vec.From(editor.getPointInShapeSpace(shape, editor.inputs.getCurrentPagePoint())),
    };
    editor.setCursor({ type: "move", rotation: 0 });
  }
  override onPointerMove() {
    const editor = ed(this);
    if (!this.start) return;
    const { shape, full, point } = this.start;
    const now = Vec.From(editor.getPointInShapeSpace(shape, editor.inputs.getCurrentPagePoint()));
    const visible = new Box(0, 0, shape.props.w, shape.props.h);
    // The image moves with the pointer but always covers the crop window.
    const dx = Math.min(-full.minX, Math.max(visible.maxX - full.maxX, now.x - point.x));
    const dy = Math.min(-full.minY, Math.max(visible.maxY - full.maxY, now.y - point.y));
    const moved = new Box(full.minX + dx, full.minY + dy, full.w, full.h);
    editor.updateShape({
      id: shape.id,
      type: "image",
      props: { crop: cropFor(shape, visible, moved) },
    } as TLShapePartial);
  }
  override onPointerUp() {
    this.parent.transition("idle");
  }
  override onCancel() {
    ed(this).bailToMark(this.mark);
    this.parent.transition("idle");
  }
  override onKeyDown(info: KeyboardEventInfo) {
    if (info.key === "Escape") this.onCancel();
  }
}

class Cropping extends StateNode {
  static override id = "cropping";
  private mark = "";
  private handle: ResizeHandle = "bottom_right";
  private start?: { shape: TLImageShape; full: Box; page: Mat };
  override onEnter(info: PointerEventInfo & { target: "selection" }) {
    const editor = ed(this);
    const shape = croppingShape(editor);
    if (!shape) return this.parent.transition("idle");
    this.mark = editor.markHistoryStoppingPoint("crop image", { hold: true });
    this.handle = info.handle as ResizeHandle;
    this.start = { shape, full: fullImageBox(shape), page: editor.getShapePageTransform(shape) };
  }
  override onPointerMove() {
    const editor = ed(this);
    if (!this.start) return;
    const { shape, full, page } = this.start;
    const p = page.invert().applyToPoint(editor.inputs.getCurrentPagePoint());
    const h = this.handle;
    let minX = 0,
      minY = 0,
      maxX = shape.props.w,
      maxY = shape.props.h;
    // Edges stop at the image's own edges and never cross each other.
    if (h.includes("left")) minX = Math.min(maxX - MIN_CROP, Math.max(full.minX, p.x));
    if (h.includes("right")) maxX = Math.max(minX + MIN_CROP, Math.min(full.maxX, p.x));
    if (h.includes("top")) minY = Math.min(maxY - MIN_CROP, Math.max(full.minY, p.y));
    if (h.includes("bottom")) maxY = Math.max(minY + MIN_CROP, Math.min(full.maxY, p.y));
    const visible = new Box(minX, minY, maxX - minX, maxY - minY);
    // The image stays where it is on the page; the shape's box moves over it.
    const origin = editor.getPointInParentSpace(shape.id, page.applyToPoint({ x: minX, y: minY }));
    const shifted = new Box(full.minX - minX, full.minY - minY, full.w, full.h);
    editor.updateShape({
      id: shape.id,
      type: "image",
      x: origin.x,
      y: origin.y,
      props: {
        w: visible.w,
        h: visible.h,
        crop: cropFor(shape, new Box(0, 0, visible.w, visible.h), shifted),
      },
    } as TLShapePartial);
  }
  override onPointerUp() {
    this.parent.transition("idle");
  }
  override onCancel() {
    ed(this).bailToMark(this.mark);
    this.parent.transition("idle");
  }
  override onKeyDown(info: KeyboardEventInfo) {
    if (info.key === "Escape") this.onCancel();
  }
}

export class CropState extends StateNode {
  static override id = "crop";
  static override initial = "idle";
  static override children = (): (typeof StateNode & { id: string })[] => [
    CropIdle,
    TranslatingCrop,
    Cropping,
  ];
}

export class SelectTool extends StateNode {
  static override id = "select";
  static override initial = "idle";
  static override children = (): (typeof StateNode & { id: string })[] => [
    Idle,
    PointingCanvas,
    PointingShape,
    PointingSelection,
    Brushing,
    Translating,
    PointingResizeHandle,
    Resizing,
    PointingRotateHandle,
    Rotating,
    PointingHandle,
    DraggingHandle,
    EditingShape,
    CropState,
  ];
  override onCancel() {
    if (this.getCurrent()?.id === "idle") ed(this).selectNone();
  }
}
